import {
  STOCK_UPDATE_MODES,
  STOCK_UPDATE_MODE_LABELS,
  STOCK_UPDATE_REASONS,
  adjustmentReasonLabel,
  parsePastedStockList,
  stockUpdateResult,
  type Id,
  type StockLookupRow,
  type StockUpdateMode,
  type StockUpdateReason,
  type StockUpdateResult,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Minus, Plus, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";

/**
 * Inventory / Update stock (#602): load counts and corrections for one
 * warehouse as lines of "SKU, how, number", review once, apply once.
 *
 * The real counts are on paper, so the screen is built to be keyed at speed:
 * Enter on the SKU box moves to the number, Enter on the number adds the line
 * and returns to the SKU box, a barcode scanner types into the SKU box, and a
 * whole list can be pasted. Nothing changes until Apply; Apply sends the lines
 * through the ordinary stock-adjustment write (Shopify accepts first, the local
 * figure comes from its answer) and reports each line by itself.
 *
 * It works at 390 px: finger-sized controls, one column, and the summary and
 * Review and apply in a bar fixed to the bottom.
 */

interface Line {
  variantId: Id;
  sku: string;
  name: string;
  current: number;
  quantity: number;
  mode: StockUpdateMode;
  /** Why the last apply refused this line; cleared when it is edited. */
  refusal?: string;
}

const MODE_HINTS: Record<StockUpdateMode, string> = {
  set: "I counted",
  add: "+ arrived",
  remove: "− gone",
};
const QUANTITY_LABELS: Record<StockUpdateMode, string> = {
  set: "Counted",
  add: "Arrived",
  remove: "Removed",
};
const MODE_TONE: Record<StockUpdateMode, string> = {
  set: "bg-tint-blue text-tint-blue-foreground",
  add: "bg-tint-green text-tint-green-foreground",
  remove: "bg-destructive/10 text-destructive",
};
const SYMBOL: Record<StockUpdateMode, string> = {
  set: "=",
  add: "+",
  remove: "−",
};

const newApplyId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `apply-${String(Date.now())}-${String(Math.random()).slice(2, 10)}`;

function nameOf(row: StockLookupRow): string {
  return row.variantTitle
    ? `${row.productTitle} · ${row.variantTitle}`
    : row.productTitle;
}

function newOf(line: Line): number {
  return stockUpdateResult(line.mode, line.current, line.quantity);
}

export function UpdateStock() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const skuInput = useRef<HTMLInputElement>(null);
  const quantityInput = useRef<HTMLInputElement>(null);
  const listId = useId();
  const skuId = useId();
  const quantityId = useId();

  const [chosenWarehouse, setWarehouseId] = useState<Id | null>(null);
  const [reason, setReason] = useState<StockUpdateReason>("stock_take");
  const [mode, setMode] = useState<StockUpdateMode>("set");
  const [lines, setLines] = useState<Line[]>([]);
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const [paste, setPaste] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [outcome, setOutcome] = useState<StockUpdateResult | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const applyId = useRef<string | null>(null);

  const warehouses = useQuery({
    queryKey: ["inventory", "update-stock", "warehouses"],
    queryFn: async () =>
      (await data.warehouses.list({ limit: 100 })).items.filter(
        (warehouse) => warehouse.isActive,
      ),
  });
  // The chosen warehouse, else the first active one.
  const warehouseId = chosenWarehouse ?? warehouses.data?.[0]?.id ?? null;

  // The SKU box's suggestions: the warehouse's own variants, found as typed.
  const [typed, setTyped] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => {
      setTyped(sku.trim());
    }, 150);
    return () => {
      clearTimeout(handle);
    };
  }, [sku]);
  const suggestions = useQuery({
    queryKey: ["inventory", "update-stock", "suggest", warehouseId, typed],
    queryFn: () =>
      data.inventory.lookupStock({
        warehouseId: warehouseId ?? "",
        search: typed,
      }),
    enabled: warehouseId !== null && typed.length >= 1,
  });
  const lowest = useQuery({
    queryKey: ["inventory", "update-stock", "lowest", warehouseId],
    queryFn: () =>
      data.inventory.lookupStock({ warehouseId: warehouseId ?? "", lowest: 5 }),
    enabled: warehouseId !== null,
  });

  const summary = useMemo(() => {
    const changing = lines.filter((line) => newOf(line) !== line.current);
    return {
      lines: lines.length,
      changing,
      added: changing
        .filter((line) => newOf(line) > line.current)
        .reduce((sum, line) => sum + newOf(line) - line.current, 0),
      removed: changing
        .filter((line) => newOf(line) < line.current)
        .reduce((sum, line) => sum + line.current - newOf(line), 0),
      belowZero: lines.filter((line) => newOf(line) < 0).length,
    };
  }, [lines]);

  function addLine(row: StockLookupRow, count: number) {
    const line: Line = {
      variantId: row.variantId,
      sku: row.sku ?? "",
      name: nameOf(row),
      current: row.available,
      quantity: count,
      mode,
    };
    setLines((current) => [
      line,
      ...current.filter((existing) => existing.variantId !== row.variantId),
    ]);
    setOutcome(null);
  }

  function flash(message: string) {
    setHint(message);
    setTimeout(() => {
      setHint(null);
    }, 3200);
  }

  async function submitLine() {
    if (warehouseId === null) return;
    const wanted = sku.trim().toUpperCase();
    const count = Number(quantity);
    if (wanted === "") {
      flash("Type or scan a SKU.");
      return;
    }
    if (quantity.trim() === "" || !Number.isInteger(count) || count < 0) {
      quantityInput.current?.focus();
      flash("Enter a whole number.");
      return;
    }
    const known = suggestions.data?.find(
      (row) => (row.sku ?? "").toUpperCase() === wanted,
    );
    const [row] = known
      ? [known]
      : await data.inventory.lookupStock({ warehouseId, skus: [wanted] });
    if (!row) {
      flash(`No SKU “${wanted}” at this warehouse.`);
      skuInput.current?.focus();
      return;
    }
    addLine(row, count);
    setSku("");
    setQuantity("");
    skuInput.current?.focus();
  }

  async function addPasted() {
    if (warehouseId === null) return;
    const { lines: parsed, unreadable } = parsePastedStockList(paste);
    const rows =
      parsed.length === 0
        ? []
        : await data.inventory.lookupStock({
            warehouseId,
            skus: parsed.map((entry) => entry.sku),
          });
    const bySku = new Map(
      rows.map((row) => [(row.sku ?? "").toUpperCase(), row]),
    );
    const left: string[] = [...unreadable];
    let added = 0;
    for (const entry of parsed) {
      const row = bySku.get(entry.sku);
      if (row) {
        addLine(row, entry.quantity);
        added += 1;
      } else {
        left.push(`${entry.sku} ${String(entry.quantity)}`);
      }
    }
    setPaste(left.join("\n"));
    flash(
      `${String(added)} added${left.length > 0 ? `, ${String(left.length)} left in the box (not found or unreadable)` : ""}.`,
    );
  }

  const apply = useMutation({
    mutationFn: () => {
      applyId.current ??= newApplyId();
      return data.inventory.applyStockUpdate({
        warehouseId: warehouseId ?? "",
        reason,
        applyId: applyId.current,
        lines: summary.changing.map((line) => ({
          variantId: line.variantId,
          mode: line.mode,
          quantity: line.quantity,
        })),
      });
    },
    onSuccess: (result) => {
      if (!result.ok) {
        // Nothing was written: keep the apply id so a retry is the same apply.
        setRejection(result.rejection);
        return;
      }
      applyId.current = null;
      setRejection(null);
      setReviewing(false);
      setOutcome(result.data);
      const refused = new Map(
        result.data.lines
          .filter((line) => line.status === "rejected")
          .map((line) => [line.variantId, line.message ?? "Refused."]),
      );
      // Applied and unchanged lines are done; a refused one stays, with why.
      setLines((current) =>
        current
          .filter((line) => refused.has(line.variantId))
          .map((line) => ({ ...line, refusal: refused.get(line.variantId) })),
      );
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  if (warehouses.isError) {
    return (
      <LoadFailed
        message={warehouses.error.message}
        onRetry={() => {
          void warehouses.refetch();
        }}
      />
    );
  }

  const canApply = summary.changing.length > 0 && warehouseId !== null;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 pb-28 lg:pb-4">
      <div>
        <h2 className="text-lg font-semibold">Update stock</h2>
        <p className="text-muted-foreground text-sm">
          Type or scan a SKU, enter a number, press Enter. Review once, apply
          once. Nothing changes until you apply.
        </p>
      </div>

      {outcome ? <OutcomeBanner outcome={outcome} reason={reason} /> : null}

      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 flex-1 space-y-1 sm:flex-none">
          <Label className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            Warehouse
          </Label>
          <Select
            value={warehouseId ?? undefined}
            onValueChange={(next) => {
              setWarehouseId(next);
              setLines([]);
              setOutcome(null);
            }}
          >
            <SelectTrigger className="h-11 w-full sm:w-64">
              <SelectValue placeholder="Choose a warehouse" />
            </SelectTrigger>
            <SelectContent>
              {(warehouses.data ?? []).map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 flex-1 space-y-1 sm:flex-none">
          <Label className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            Reason
          </Label>
          <Select
            value={reason}
            onValueChange={(next) => {
              setReason(next as StockUpdateReason);
            }}
          >
            <SelectTrigger className="h-11 w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STOCK_UPDATE_REASONS.map((value) => (
                <SelectItem key={value} value={value}>
                  {adjustmentReasonLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
        <div className="min-w-0 space-y-3">
          <div className="space-y-3 rounded-xl border p-3.5">
            <div
              role="group"
              aria-label="How to read the number"
              className="bg-muted grid grid-cols-3 gap-1.5 rounded-lg p-1"
            >
              {STOCK_UPDATE_MODES.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={mode === value}
                  onClick={() => {
                    setMode(value);
                  }}
                  className={cn(
                    "flex h-11 flex-col items-center justify-center rounded-md text-sm leading-tight font-bold",
                    mode === value ? SELECTED_CHOICE : "text-muted-foreground",
                  )}
                >
                  {STOCK_UPDATE_MODE_LABELS[value]}
                  <span className="text-[0.6875rem] font-medium">
                    {MODE_HINTS[value]}
                  </span>
                </button>
              ))}
            </div>

            <form
              autoComplete="off"
              className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_8rem_auto]"
              onSubmit={(event) => {
                event.preventDefault();
                void submitLine();
              }}
            >
              <div className="space-y-1">
                <Label
                  htmlFor={skuId}
                  className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
                >
                  SKU
                </Label>
                <Input
                  id={skuId}
                  ref={skuInput}
                  list={listId}
                  value={sku}
                  autoCapitalize="characters"
                  placeholder="WT04"
                  className="h-11 font-mono uppercase"
                  onChange={(event) => {
                    setSku(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && quantity === "") {
                      event.preventDefault();
                      quantityInput.current?.focus();
                    }
                  }}
                />
                <datalist id={listId}>
                  {(suggestions.data ?? []).map((row) => (
                    <option key={row.variantId} value={row.sku ?? ""}>
                      {nameOf(row)} ({String(row.available)})
                    </option>
                  ))}
                </datalist>
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor={quantityId}
                  className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
                >
                  {QUANTITY_LABELS[mode]}
                </Label>
                <Input
                  id={quantityId}
                  ref={quantityInput}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={quantity}
                  placeholder="0"
                  className="h-11"
                  onChange={(event) => {
                    setQuantity(event.target.value);
                  }}
                />
              </div>
              <Button
                type="submit"
                className="col-span-2 h-11 sm:col-span-1"
                disabled={warehouseId === null}
              >
                Add
              </Button>
            </form>
            <p
              className={cn(
                "text-xs",
                hint ? "text-destructive" : "text-muted-foreground",
              )}
              aria-live="polite"
            >
              {hint ??
                (mode === "set"
                  ? "Set to: the shelf has exactly this many. Best for a stock take."
                  : mode === "add"
                    ? "Add: this many arrived. The new number is the current number plus this."
                    : "Remove: this many left the shelf. The new number is the current number minus this.")}
            </p>

            {(lowest.data ?? []).length > 0 ? (
              <div
                className="flex flex-wrap gap-1.5"
                aria-label="Lowest stock first"
              >
                {(lowest.data ?? []).map((row) => (
                  <button
                    key={row.variantId}
                    type="button"
                    className="bg-muted hover:bg-accent min-h-9 rounded-full border px-2.5 py-1 text-xs"
                    onClick={() => {
                      setSku(row.sku ?? "");
                      quantityInput.current?.focus();
                    }}
                  >
                    <b className="font-mono font-medium">{row.sku}</b> now{" "}
                    <span
                      className={row.available < 0 ? "text-destructive" : ""}
                    >
                      {row.available}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            <details className="rounded-lg border border-dashed p-2.5 text-sm">
              <summary className="cursor-pointer font-semibold">
                Paste a list
              </summary>
              <p className="text-muted-foreground mt-1 text-xs">
                One per line: SKU, then number. Uses the mode above. Commas,
                tabs or spaces all work. A SKU that is not found stays in the
                box.
              </p>
              <textarea
                value={paste}
                rows={4}
                placeholder={"WT04 12\nWT09 7\nRG06 20"}
                className="bg-background mt-2 w-full rounded-md border p-2 font-mono text-sm"
                onChange={(event) => {
                  setPaste(event.target.value);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="mt-2 h-11"
                disabled={paste.trim() === "" || warehouseId === null}
                onClick={() => {
                  void addPasted();
                }}
              >
                Add these lines
              </Button>
            </details>
          </div>

          <ul className="space-y-2" aria-label="Lines to apply">
            {lines.length === 0 ? (
              <li className="text-muted-foreground rounded-xl border border-dashed p-5 text-center text-sm">
                Nothing added yet. Start with a SKU above, or tap one of the
                lowest-stock items.
              </li>
            ) : null}
            {lines.map((line) => (
              <LineRow
                key={line.variantId}
                line={line}
                onChange={(next) => {
                  setLines((current) =>
                    current.map((existing) =>
                      existing.variantId === line.variantId
                        ? { ...next, refusal: undefined }
                        : existing,
                    ),
                  );
                }}
                onRemove={() => {
                  setLines((current) =>
                    current.filter(
                      (existing) => existing.variantId !== line.variantId,
                    ),
                  );
                }}
              />
            ))}
          </ul>
        </div>

        <aside
          aria-live="polite"
          className="hidden space-y-2 rounded-xl border p-3.5 lg:sticky lg:top-3 lg:block"
        >
          <h3 className="text-sm font-semibold">This update</h3>
          <Totals summary={summary} />
          <Button
            className="h-11 w-full"
            disabled={!canApply}
            onClick={() => {
              setRejection(null);
              setReviewing(true);
            }}
          >
            Review and apply
          </Button>
        </aside>
      </div>

      <div className="bg-background fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t px-4 py-2.5 lg:hidden">
        <div className="min-w-0 flex-1 text-sm">
          {summary.lines > 0 ? (
            <>
              <b>
                {summary.lines} line{summary.lines === 1 ? "" : "s"}
              </b>{" "}
              · {summary.changing.length} will change
              {summary.belowZero > 0 ? (
                <span className="text-destructive">
                  {" "}
                  · {summary.belowZero} below zero
                </span>
              ) : null}
            </>
          ) : (
            "No lines yet"
          )}
        </div>
        <Button
          className="h-12 px-5 font-bold"
          disabled={!canApply}
          onClick={() => {
            setRejection(null);
            setReviewing(true);
          }}
        >
          Review and apply
        </Button>
      </div>

      {reviewing ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !apply.isPending) setReviewing(false);
          }}
        >
          <DialogContent
            title="Review and apply"
            description={`${String(summary.changing.length)} change${summary.changing.length === 1 ? "" : "s"}, reason “${adjustmentReasonLabel(reason)}”.`}
          >
            <div className="space-y-3">
              <ul className="divide-y rounded-md border text-sm">
                {summary.changing.map((line) => (
                  <li
                    key={line.variantId}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="min-w-0 truncate font-mono">
                      {line.sku}
                    </span>
                    <span className="font-mono tabular-nums">
                      {line.current} →{" "}
                      <b className={newOf(line) < 0 ? "text-destructive" : ""}>
                        {newOf(line)}
                      </b>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground text-xs">
                Shopify accepts each change first, then the ERP is updated from
                its answer. A refused line changes nothing and is reported by
                itself.
              </p>
              {rejection ? (
                <p role="alert" className="text-destructive text-sm">
                  {rejection.message}
                </p>
              ) : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="ghost"
                  disabled={apply.isPending}
                  onClick={() => {
                    setReviewing(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  disabled={apply.isPending}
                  onClick={() => {
                    apply.mutate();
                  }}
                >
                  {apply.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : null}
                  Apply {summary.changing.length} change
                  {summary.changing.length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function Totals({
  summary,
}: {
  summary: {
    lines: number;
    changing: unknown[];
    added: number;
    removed: number;
    belowZero: number;
  };
}) {
  const rows: [string, string, string?][] = [
    ["Lines", String(summary.lines)],
    ["Will change", String(summary.changing.length)],
    ["Units added", `+${String(summary.added)}`, "text-tint-green-foreground"],
    ["Units removed", `−${String(summary.removed)}`, "text-destructive"],
    [
      "Below zero",
      String(summary.belowZero),
      summary.belowZero > 0 ? "text-destructive" : undefined,
    ],
  ];
  return (
    <dl className="text-sm">
      {rows.map(([label, value, tone]) => (
        <div
          key={label}
          className="flex justify-between border-b py-1.5 last:border-0"
        >
          <dt>{label}</dt>
          <dd className={cn("font-semibold tabular-nums", tone)}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function OutcomeBanner({
  outcome,
  reason,
}: {
  outcome: StockUpdateResult;
  reason: StockUpdateReason;
}) {
  return (
    <div
      role="status"
      className={cn(
        "rounded-lg p-3 text-sm font-medium",
        outcome.rejected > 0
          ? "bg-tint-yellow text-foreground"
          : "bg-tint-green text-tint-green-foreground",
      )}
    >
      Applied {outcome.applied} change{outcome.applied === 1 ? "" : "s"}
      {outcome.unchanged > 0
        ? `, ${String(outcome.unchanged)} already right`
        : ""}
      . Each one is in the stock history with the reason “
      {adjustmentReasonLabel(reason)}”.
      {outcome.rejected > 0
        ? ` ${String(outcome.rejected)} refused, nothing changed on those: they are still listed below with why.`
        : ""}
    </div>
  );
}

function LineRow({
  line,
  onChange,
  onRemove,
}: {
  line: Line;
  onChange: (line: Line) => void;
  onRemove: () => void;
}) {
  const next = newOf(line);
  return (
    <li
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-xl border p-3",
        line.refusal && "border-destructive/50",
      )}
    >
      <div className="min-w-0">
        <strong className="block text-sm wrap-anywhere">{line.name}</strong>
        <span className="text-muted-foreground font-mono text-xs">
          {line.sku}
        </span>
      </div>
      <button
        type="button"
        aria-label={`Remove line ${line.sku}`}
        className="text-muted-foreground hover:bg-accent flex size-10 items-center justify-center rounded-md"
        onClick={onRemove}
      >
        <X aria-hidden className="size-5" />
      </button>
      <div className="col-span-2 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-bold",
            MODE_TONE[line.mode],
          )}
        >
          {STOCK_UPDATE_MODE_LABELS[line.mode]}
        </span>
        <div className="bg-background inline-flex items-center overflow-hidden rounded-md border">
          <button
            type="button"
            aria-label="Less"
            className="bg-muted flex size-11 items-center justify-center"
            onClick={() => {
              onChange({ ...line, quantity: Math.max(0, line.quantity - 1) });
            }}
          >
            <Minus aria-hidden className="size-4" />
          </button>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={line.quantity}
            aria-label={`Number for ${line.sku}`}
            className="h-11 w-16 border-0 text-center font-mono"
            onChange={(event) => {
              const value = Number(event.target.value);
              onChange({
                ...line,
                quantity: Number.isInteger(value) && value >= 0 ? value : 0,
              });
            }}
          />
          <button
            type="button"
            aria-label="More"
            className="bg-muted flex size-11 items-center justify-center"
            onClick={() => {
              onChange({ ...line, quantity: line.quantity + 1 });
            }}
          >
            <Plus aria-hidden className="size-4" />
          </button>
        </div>
        <span className="ml-auto flex items-baseline gap-1.5 font-mono tabular-nums">
          <span className="text-muted-foreground">{line.current}</span>
          <span>
            {line.mode === "set"
              ? "→"
              : `${SYMBOL[line.mode]} ${String(line.quantity)} →`}
          </span>
          <b className={cn("text-lg", next < 0 && "text-destructive")}>
            {next}
          </b>
        </span>
      </div>
      {next < 0 ? (
        <p className="text-destructive col-span-2 text-xs">
          This would leave {next} on the shelf. Check the number.
        </p>
      ) : null}
      {line.refusal ? (
        <p role="alert" className="text-destructive col-span-2 text-xs">
          Refused, nothing changed: {line.refusal}
        </p>
      ) : null}
    </li>
  );
}
